<script setup lang="ts">
import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic'

type ProductColorSelectorProps = {
  attribute: ShopperProductsTypes.VariationAttribute
  productStoreLocation: ProductStoreLocation
}

const props = defineProps<ProductColorSelectorProps>()
const productStore = useProductStore(props.productStoreLocation)
const { t } = useI18n()

const colorAttribute = computed(() =>
  props.attribute?.values?.find((attr) => {
    return attr.value === productStore.selectedProduct?.variationValues?.[props.attribute.id]
  }),
)

const colorSelected = ref([
  productStore.selectedProduct?.c_metalColour ?? colorAttribute.value?.name,
  colorAttribute.value?.value,
])

const getAttributeDisplayName = () => {
  // unable to change the attribute names in SFCC due to comestri overriding it with each product push. Reconfiguring comestri to update the attribute names
  // is highly risky as it will affect all products structure. so doing this workaround instead to show correct messaging for metal colours.
  if (
    props.attribute.id === 'PIMColourMetal' ||
    props.attribute.id === 'PIMColourGold' ||
    props.attribute.id === 'PIMColourPWBRings'
  ) {
    return t('product.variant_selector.colour')
  } else {
    return props.attribute.name
  }
}

const valueName = (values: ShopperProductsTypes.VariationAttributeValue) => {
  if (values !== null) {
    const { name, value } = values
    return [name, value]
  }
  return []
}

const onColorSelected = async () => {
  const newVariant = productStore.selectedProduct?.variants?.find((variant) => {
    return variant.variationValues?.[props.attribute.id] === colorSelected.value[1]
  })
  if (!newVariant) return
  await productStore.getNewVariant(newVariant.productId)
}
</script>

<template>
  <div class="product-color-selector">
    <div class="product-color-selector__attribute-name">
      <span data-testid="attributeName">{{ getAttributeDisplayName() }}:</span>
      <span class="product-color-selector__color-names" data-testid="selectedColor">{{ colorSelected[0] }}</span>
    </div>
    <div v-if="attribute && attribute.values" class="product-color-selector__metal-selector-wrapper">
      <label
        v-for="value in attribute.values"
        :key="value.name"
        :for="`product-color-selector-${value.value}`"
        class="product-color-selector__label"
      >
        <input
          :id="`product-color-selector-${value.value}`"
          v-model="colorSelected"
          type="radio"
          name="product-color-selector"
          class="product-color-selector__input"
          :value="valueName(value)"
          :disabled="!value.orderable"
          @change="onColorSelected"
        />
        <svg-icon
          :name="`swatches:${getSpriteName({ attrName: attribute.id, value: value.name, id: value.value })}`"
          class="product-color-selector__swatch"
        />

        <span class="sr-only">{{ value.name }}</span>
      </label>
    </div>
  </div>
</template>

<style lang="postcss">
.product-color-selector {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  row-gap: var(--space-20);
  column-gap: var(--space-30);

  .product-color-selector__attribute-name {
    text-transform: uppercase;
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-10);
    font-size: var(--text-small);
  }

  .product-color-selector__metal-selector-wrapper {
    display: flex;
    flex-wrap: wrap;
    padding-top: var(--space-20);
    padding-bottom: var(--space-20);
    gap: var(--space-30);
    position: relative;
  }
  .product-color-selector__label {
    position: relative;
    width: 1.25rem;
    height: 1.25rem;
  }
  .product-color-selector__input {
    appearance: none;
    -webkit-appearance: none;
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 100%;
    position: relative;
    &:hover {
      cursor: pointer;
    }
    &:disabled {
      background-color: var(--text-disabled);
      opacity: 50%;
      width: 1.25rem;
      height: 1.25rem;
      border-radius: 100%;
      display: block;
      z-index: 1;
      cursor: not-allowed;
      &::before {
        content: '';
        background-color: black;
        width: 1px;
        height: var(--space-70);
        position: absolute;
        bottom: -1px;
        right: -1px;
        rotate: -45deg;
        transform-origin: bottom right;
      }
    }
    &:checked::after {
      content: '';
      display: block;
      width: 1.25rem;
      height: 1.25rem;
      border-radius: 100%;
      outline: 1px solid #000;
      outline-offset: 2px;
    }
    &:focus-visible {
      outline: 1px solid var(--status-info);
      outline-offset: 4px;
    }
  }
  .product-color-selector__swatch {
    position: absolute;
    top: 0;
    left: 0;
    height: 1.25rem;
    width: 1.25rem;
    pointer-events: none;
  }
}
</style>
